import { errorMessage } from "../../utils/mutationResult.js";
import { withoutKeyIssues } from "../../utils/keyIssueMatrix";
import { saveLinkedKeyIssue as updateObservationWithRiskMatrix, createObservationWithKeyIssues as createObservationForRiskMatrix } from "./keyIssueMatrixSlice";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneFiles, removePendingFilesByClientIds } from "./fileSlice.js";
import {
  fetchOrQueue,
  cancelOutboxItemsForEntity,
} from "../../lib/offlineQueue.js";
import { getAllOutboxItems, updateOutboxItem, getCachedData } from "../../lib/offlineDb.js";
import { upsertOutboxItem } from "./offlineSlice.js";
import { resolveEntityId, canonicalEntityId, isTemporaryEntityId, readLocalReportEntities, createReportReadGuard } from "../../lib/syncedEntityIds";
import { matchesObservationUpdate, reconcileObservationUpdate } from "../../utils/observationIdentity";

// Initial state
const initialState = {
  observations: [],
  observationsBySite: [], // For risk matrix dropdown
  currentObservation: null,
  observationStats: null,
  loading: false,
  loadingRiskMatrix: false, // Loading state for risk matrix operations
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  filters: {
    projectId: "",
    observationType: "",
    severity: "",
    status: "",
    observedBy: "",
  },
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8005/api";

const getActiveReportId = () =>
  window.sessionStorage?.getItem("activeReportId") ||
  window.localStorage?.getItem("activeReportId") ||
  "";

const normalizeRiskKey = (risk) =>
  [risk?.title, risk?.description, risk?.comments]
    .map((value) =>
      String(value || "")
        .trim()
        .toLowerCase(),
    )
    .join("|");

const getImportedRiskId = (file, observationPayload, observation) => {
  const localRiskId = String(file?.relatedTo?.entityId || "");
  const pendingRisk = (observationPayload?.keyIssues || []).find(
    (risk) => String(risk?.id || risk?._id) === localRiskId,
  );
  if (!pendingRisk) return null;

  const savedRisk = (observation?.keyIssues || []).find(
    (risk) => normalizeRiskKey(risk) === normalizeRiskKey(pendingRisk),
  );
  return savedRisk?._id || null;
};

// Async thunks for observations
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchObservations = createAsyncThunk(
  "observations/fetchObservations",
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const reportId = canonicalEntityId(state, "report", params.reportId || getActiveReportId());
      const siteId = canonicalEntityId(state, "site", params.siteId);
      params = { ...params, ...(siteId ? { siteId } : {}) };
      const localDuringSync = createReportReadGuard(getState, reportId);
      if (localDuringSync() || isTemporaryEntityId(reportId) || isTemporaryEntityId(siteId)) {
        const observations = await readLocalReportEntities(state, "observations", reportId, siteId, params.templateId);
        return { observations, pagination: { currentPage: 1, totalPages: 1, totalItems: observations.length, itemsPerPage: observations.length } };
      }
      const queryParams = new URLSearchParams({
        ...params,
        ...(reportId ? { reportId } : {}),
      }).toString();
      const response = await fetch(
        `${API_BASE_URL}/observations?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(errorMessage(error, "Failed to fetch observations"));
      }

      const data = await response.json();
      if (localDuringSync()) {
        const observations = await readLocalReportEntities(getState(), "observations", reportId, siteId, params.templateId);
        return { observations, pagination: { currentPage: 1, totalPages: 1, totalItems: observations.length, itemsPerPage: observations.length } };
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchObservationById = createAsyncThunk(
  "observations/fetchObservationById",
  async (observationId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/observations/${observationId}?populate=true`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(errorMessage(error, "Failed to fetch observation"));
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const createObservation = createAsyncThunk(
  "observations/createObservation",
  async (observationData, { rejectWithValue, dispatch }) => {
    try {
      const reportId = observationData?.reportId || getActiveReportId();
      const clientId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const {
        _pendingImportedFiles = [],
        _importedLocalId,
        _sourceId,
        _id,
        id,
        _clientId,
        clientId: _queuedClientId,
        _offlineQueued,
        keyIssues = [],
        ...observationPayload
      } = observationData || {};
      if (!observationPayload.templateId) {
        return rejectWithValue("Template ID is required");
      }
      const requestBody = JSON.stringify({
        ...observationPayload,
        ...(reportId ? { reportId } : {}),
      });
      const requestUrl = `${API_BASE_URL}/observations`;
      const requestHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      // Phase 2: if offline, queue the create and let the auditor keep
      // working with an optimistic local observation. It's reconciled with
      // the real server record once the sync manager replays the outbox.
      const outcome = await fetchOrQueue({
        dispatch,
        forceQueue: String(reportId).startsWith("offline") || String(observationPayload.siteId).startsWith("offline"),
        requestInit: {
          url: requestUrl,
          method: "POST",
          headers: requestHeaders,
          body: requestBody,
        },
        meta: {
          description: `Create observation ${observationPayload?.title || ""}`,
          entityType: "observation",
          entityId: clientId,
          clientId,
          requestType: "observation-create",
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "POST",
            headers: requestHeaders,
            credentials: "include",
            body: requestBody,
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to create observation"),
            );
            err.status = response.status;
            throw err;
          }
          return response.json();
        },
      });

      const observationRecord = outcome.queued
        ? { ...observationPayload, _id: clientId, _clientId: clientId, reportId, _offlineQueued: true }
        : outcome.result.data;
      const { mutateKeyIssueMatrix } = await import("./keyIssueMatrixSlice");
      const savedIssues = [];
      for (const riskData of keyIssues) {
        const { item } = await dispatch(mutateKeyIssueMatrix({ observationId: observationRecord._id,
          observation: observationRecord, action: "create", riskData })).unwrap();
        savedIssues.push(item);
      }
      const result = { ...observationRecord, keyIssues: savedIssues };
      if (_pendingImportedFiles.length > 0 && observationRecord._id) {
        const filesToClone = _pendingImportedFiles
          .map((file) => {
            const isRiskFile =
              file?.relatedTo?.entityType === "key-issue-matrix";
            const entityId = isRiskFile
              ? getImportedRiskId(file, { ...observationPayload, keyIssues }, result)
              : observationRecord._id;
            if (!entityId) return null;

            return {
              ...file,
              relatedTo: {
                ...(file.relatedTo || {}),
                entityType: isRiskFile ? "key-issue-matrix" : "observation",
                entityId,
              },
            };
          })
          .filter(Boolean);

        if (filesToClone.length === 0) return result;

        await dispatch(
          cloneFiles({
            files: filesToClone,
            siteId: observationPayload.siteId,
            reportId,
          }),
        ).unwrap();
      }
      return result;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const updateObservation = createAsyncThunk(
  "observations/updateObservation",
  async (
    { observationId, observationData },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      observationId = await resolveEntityId(getState(), "observation", observationId);
      const reportId = observationData?.reportId || getActiveReportId();
      const {
        _pendingImportedFiles = [],
        _importedLocalId,
        _sourceId,
        ...observationPayload
      } = observationData || {};
      const requestBody = JSON.stringify({
        ...observationPayload,
        ...(reportId ? { reportId } : {}),
      });

      // An offline-created observation has no server endpoint yet. Merge
      // edits into its queued create instead of queueing a PUT to the local
      // id; queueing that PUT causes a second observation after replay.
      if (String(observationId).startsWith("offline_")) {
        const queuedItems = await getAllOutboxItems();
        const createItem = queuedItems.find(
          (item) =>
            item.type === "observation-create" &&
            item.status !== "syncing" &&
            (item.meta?.clientId === observationId ||
              item.meta?.entityId === observationId),
        );
        if (createItem) {
          const currentBody = JSON.parse(createItem.body || "{}");
          const updatedItem = await updateOutboxItem(createItem.id, {
            status: "pending", retryable: true, retryCount: 0, lastError: null, errorKind: null,
            body: JSON.stringify({
              ...currentBody,
              ...observationPayload,
              ...(reportId ? { reportId } : {}),
            }),
          });
          console.log("current body and updated item for offline observation update:", currentBody, updatedItem);
          if (updatedItem) dispatch(upsertOutboxItem(updatedItem));
          return {
            ...observationPayload,
            _id: observationId,
            _clientId: observationId,
            reportId,
            _offlineQueued: true,
          };
        }
      }

      const requestUrl = `${API_BASE_URL}/observations/${observationId}`;
      const requestHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      // Phase 2: queue this update in the outbox when offline so the
      // observation's edits are never lost, then apply them optimistically.
      const outcome = await fetchOrQueue({
        dispatch,
        requestInit: {
          url: requestUrl,
          method: "PUT",
          headers: requestHeaders,
          body: requestBody,
        },
        meta: {
          description: `Update observation ${observationId}`,
          entityType: "observation",
          entityId: observationId,
          requestType: "observation-update",
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "PUT",
            headers: requestHeaders,
            credentials: "include",
            body: requestBody,
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to update observation"),
            );
            err.status = response.status;
            throw err;
          }
          return response.json();
        },
      });

      if (outcome.queued) {
        const existing = getState()?.observations?.observations?.find(
          (o) => o._id === observationId || o._clientId === observationId,
        );
        return {
          ...(existing || {}),
          ...observationPayload,
          _id: existing?._id || observationId,
          _clientId: existing?._clientId,
          _offlineQueued: true,
        };
      }

      const data = outcome.result;
      return data?.data?.observation || data?.data || data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const deleteObservation = createAsyncThunk(
  "observations/deleteObservation",
  async (observationId, { rejectWithValue, dispatch, getState }) => {
    try {
      const existing = getState()?.observations?.observations?.find(
        (o) => o._id === observationId || o._clientId === observationId,
      );
      const entityId = existing?._id || observationId;
      const clientId = existing?._clientId || entityId;
      const reports = Object.values(getState()?.auditReports?.allReportsData || {});
      const childEntities = reports.flatMap((report) => report?.keyIssueMatrixItems || [])
        .filter((item) => [entityId, clientId].includes(item.observationId))
        .map((item) => ({ entityType: "key-issue-matrix", entityId: item._id }));
      // Phase 2: an observation created entirely offline never reached the
      // server (its id is still the local "offline_..." clientId), so
      // deleting it means cancelling the queued create — and any queued
      // edits or image uploads made to it before the delete — instead of
      // sending a delete request the server has no matching record for.
      const neverSynced = String(entityId).startsWith("offline");

      if (neverSynced) {
        const cancelled = await cancelOutboxItemsForEntity(dispatch, {
          entityType: "observation",
          entityId,
          clientId,
          childEntities,
        });
        const cancelledFileClientIds = cancelled
          .filter((item) => item.type === "file-upload" || item.type === "file-update" || item.type === "file-delete")
          .map((item) => item.clientId);
        if (cancelledFileClientIds.length > 0) {
          dispatch(removePendingFilesByClientIds(cancelledFileClientIds));
        }
        return observationId;
      }

      // Deleting supersedes any not-yet-synced edit still queued for this
      // observation — cancel it so it doesn't try to PATCH an entity that's
      // about to be gone.
      await cancelOutboxItemsForEntity(dispatch, {
        entityType: "observation",
        entityId,
        clientId,
        childEntities,
      });

      const requestUrl = `${API_BASE_URL}/observations/${observationId}`;
      const requestHeaders = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      // Phase 2: queue the delete in the outbox when offline so it replays
      // once connectivity returns, applying it optimistically in the
      // meantime so the observation disappears from the UI right away.
      await fetchOrQueue({
        dispatch,
        requestInit: {
          url: requestUrl,
          method: "DELETE",
          headers: requestHeaders,
        },
        meta: {
          description: `Delete observation ${observationId}`,
          entityType: "observation",
          entityId: observationId,
          requestType: "observation-delete",
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "DELETE",
            headers: requestHeaders,
            credentials: "include",
          });
          if (!response.ok) {
            const error = await response.json();
            const err = new Error(
              errorMessage(error, "Failed to delete observation"),
            );
            err.status = response.status;
            throw err;
          }
          return observationId;
        },
      });

      return observationId;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

// Fetch observations by siteId for dropdown selection
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchObservationsBySiteId = createAsyncThunk(
  "observations/fetchObservationsBySiteId",
  async (siteId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const reportId = canonicalEntityId(state, "report", getActiveReportId());
      siteId = canonicalEntityId(state, "site", siteId);
      const localDuringSync = createReportReadGuard(getState, reportId);
      if (localDuringSync() || isTemporaryEntityId(reportId) || isTemporaryEntityId(siteId)) {
        return readLocalReportEntities(state, "observations", reportId, siteId);
      }
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const record = state?.auditReports?.allReportsData?.[reportId] || state?.auditReports?.currentReportData ||
          (await getCachedData("audit-reports:all-with-data") || []).find((entry) => entry.report?._id === reportId);
        return (record?.observations || state?.observations?.observations || []).filter((observation) =>
          (!reportId || observation.reportId === reportId) && (!siteId || observation.siteId === siteId));
      }
      const params = new URLSearchParams({ siteId, limit: "100" });
      if (reportId) params.append("reportId", reportId);
      const response = await fetch(
        `${API_BASE_URL}/observations?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(errorMessage(error, "Failed to fetch observations"));
      }

      const data = await response.json();
      if (localDuringSync()) return readLocalReportEntities(getState(), "observations", reportId, siteId);
      return data.data?.observations || [];
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

// Matrix mutations live in their own slice/collection.
export { saveLinkedKeyIssue as updateObservationWithRiskMatrix, createObservationWithKeyIssues as createObservationForRiskMatrix } from "./keyIssueMatrixSlice";

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const addFollowUpAction = createAsyncThunk(
  "observations/addFollowUpAction",
  async ({ observationId, actionData }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/observations/${observationId}/follow-up`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(actionData),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          errorMessage(error, "Failed to add follow-up action"),
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const updateFollowUpAction = createAsyncThunk(
  "observations/updateFollowUpAction",
  async ({ observationId, actionId, actionData }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/observations/${observationId}/follow-up/${actionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(actionData),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          errorMessage(error, "Failed to update follow-up action"),
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const resolveObservation = createAsyncThunk(
  "observations/resolveObservation",
  async ({ observationId, resolutionData }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/observations/${observationId}/resolve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(resolutionData),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          errorMessage(error, "Failed to resolve observation"),
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchObservationStats = createAsyncThunk(
  "observations/fetchObservationStats",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/observations/stats/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(
          errorMessage(error, "Failed to fetch observation statistics"),
        );
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"));
    }
  },
);

// Observation slice
const observationSlice = createSlice({
  name: "observations",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentObservation: (state, action) => {
      state.currentObservation = action.payload;
    },
    clearCurrentObservation: (state) => {
      state.currentObservation = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        projectId: "",
        observationType: "",
        severity: "",
        status: "",
        observedBy: "",
      };
    },
    updateObservationStatus: (state, action) => {
      const { observationId, status } = action.payload;
      const observation = state.observations.find(
        (o) => o._id === observationId,
      );
      if (observation) {
        observation.status = status;
      }
      if (
        state.currentObservation &&
        state.currentObservation._id === observationId
      ) {
        state.currentObservation.status = status;
      }
    },
    setObservationsFromVersion: (state, action) => {
      // Used when loading historical version data
      state.observations = action.payload;
    },
    addSyncedObservation: (state, action) => {
      const { observation, clientId } = action.payload || {};
      if (!observation?._id) return;
      const syncedObservation = clientId
        ? { ...observation, _clientId: clientId }
        : observation;
      const matchesClientObservation = (item) =>
        Boolean(clientId) &&
        (item?._clientId === clientId ||
          item?._id === clientId ||
          item?.id === clientId ||
          item?._id === `offline_${clientId}` ||
          item?.id === `offline_${clientId}`);
      const index = state.observations.findIndex(matchesClientObservation);
      if (index === -1) state.observations.unshift(syncedObservation);
      else
        state.observations[index] = {
          ...state.observations[index],
          ...syncedObservation,
        };
      if (matchesClientObservation(state.currentObservation)) {
        state.currentObservation = {
          ...state.currentObservation,
          ...syncedObservation,
        };
      }
      state.observationsBySite = state.observationsBySite.map((item) =>
        matchesClientObservation(item)
          ? { ...item, ...syncedObservation }
          : item,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch observations cases
      .addCase(fetchObservations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchObservations.fulfilled, (state, action) => {
        state.loading = false;
        state.observations = action.payload.observations;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchObservations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch observation by ID cases
      .addCase(fetchObservationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchObservationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentObservation = action.payload;
        state.error = null;
      })
      .addCase(fetchObservationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create observation cases
      .addCase(createObservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createObservation.fulfilled, (state, action) => {
        state.loading = false;
        const incoming = withoutKeyIssues(action.payload);
        const incomingId = incoming?._id || incoming?.id;
        const incomingClientId = incoming?._clientId || incoming?.clientId;
        const existingIndex = state.observations.findIndex(
          (observation) =>
            (incomingId &&
              (observation._id === incomingId ||
                observation.id === incomingId)) ||
            (incomingClientId && observation._clientId === incomingClientId),
        );
        if (existingIndex === -1) state.observations.unshift(incoming);
        else
          state.observations[existingIndex] = {
            ...state.observations[existingIndex],
            ...incoming,
          };
        state.error = null;
      })
      .addCase(createObservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update observation cases
      .addCase(updateObservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateObservation.fulfilled, (state, action) => {
        state.loading = false;
        const incoming = action.payload;
        if (!incoming) return;
        const requestedId = action.meta?.arg?.observationId;
        state.observations = reconcileObservationUpdate(state.observations, incoming, requestedId);
        if (matchesObservationUpdate(state.currentObservation, incoming, requestedId)) {
          state.currentObservation = { ...state.currentObservation, ...incoming };
        }
        state.error = null;
      })
      .addCase(updateObservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete observation cases
      .addCase(deleteObservation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteObservation.fulfilled, (state, action) => {
        state.loading = false;
        state.observations = state.observations.filter(
          (o) =>
            o._id !== action.payload &&
            o.id !== action.payload &&
            o._clientId !== action.payload,
        );
        if (
          state.currentObservation &&
          (state.currentObservation._id === action.payload ||
            state.currentObservation._clientId === action.payload)
        ) {
          state.currentObservation = null;
        }
        state.error = null;
      })
      .addCase(deleteObservation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch observations by siteId cases
      .addCase(fetchObservationsBySiteId.pending, (state) => {
        state.loadingRiskMatrix = true;
        state.error = null;
      })
      .addCase(fetchObservationsBySiteId.fulfilled, (state, action) => {
        state.loadingRiskMatrix = false;
        state.observationsBySite = action.payload;
        state.error = null;
      })
      .addCase(fetchObservationsBySiteId.rejected, (state, action) => {
        state.loadingRiskMatrix = false;
        state.error = action.payload;
        state.observationsBySite = [];
      })

      // Update observation with risk matrix cases
      .addCase(updateObservationWithRiskMatrix.pending, (state) => {
        state.loadingRiskMatrix = true;
        state.error = null;
      })
      .addCase(updateObservationWithRiskMatrix.fulfilled, (state, action) => {
        state.loadingRiskMatrix = false;
        const index = state.observations.findIndex(
          (o) => o._id === action.payload._id,
        );
        if (index !== -1) {
          state.observations[index] = {
            ...state.observations[index],
            ...withoutKeyIssues(action.payload),
          };
        }
        state.error = null;
      })
      .addCase(updateObservationWithRiskMatrix.rejected, (state, action) => {
        state.loadingRiskMatrix = false;
        state.error = action.payload;
      })

      // Create observation for risk matrix cases
      .addCase(createObservationForRiskMatrix.pending, (state) => {
        state.loadingRiskMatrix = true;
        state.error = null;
      })
      .addCase(createObservationForRiskMatrix.fulfilled, (state) => { state.loadingRiskMatrix = false; })
      .addCase(createObservationForRiskMatrix.rejected, (state, action) => {
        state.loadingRiskMatrix = false;
        state.error = action.payload;
      })

      // Add follow-up action cases
      .addCase(addFollowUpAction.fulfilled, (state, action) => {
        if (
          state.currentObservation &&
          state.currentObservation._id === action.payload._id
        ) {
          state.currentObservation = action.payload;
        }
        const index = state.observations.findIndex(
          (o) => o._id === action.payload._id,
        );
        if (index !== -1) {
          state.observations[index] = withoutKeyIssues(action.payload);
        }
      })

      // Update follow-up action cases
      .addCase(updateFollowUpAction.fulfilled, (state, action) => {
        if (
          state.currentObservation &&
          state.currentObservation._id === action.payload._id
        ) {
          state.currentObservation = action.payload;
        }
        const index = state.observations.findIndex(
          (o) => o._id === action.payload._id,
        );
        if (index !== -1) {
          state.observations[index] = withoutKeyIssues(action.payload);
        }
      })

      // Resolve observation cases
      .addCase(resolveObservation.fulfilled, (state, action) => {
        const incoming = action.payload;
        const index = state.observations.findIndex(
          (o) => o._id === incoming?._id,
        );
        if (index === -1) state.observations.unshift(incoming);
        else
          state.observations[index] = {
            ...state.observations[index],
            ...incoming,
          };
        if (
          state.currentObservation &&
          state.currentObservation._id === incoming?._id
        ) {
          state.currentObservation = {
            ...state.currentObservation,
            ...incoming,
          };
        }
      })

      // Fetch observation stats cases
      .addCase(fetchObservationStats.fulfilled, (state, action) => {
        state.observationStats = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentObservation,
  clearCurrentObservation,
  setFilters,
  clearFilters,
  updateObservationStatus,
  setObservationsFromVersion,
  addSyncedObservation,
} = observationSlice.actions;

export default observationSlice.reducer;
